// 🩺 치과 설정 — 「장착 후 확인」 (2026-10-02 대표 승인, 플래그 patientFeedback strict, 배포 전 OFF)
// 기획 docs/기획서_환자피드백_2026-10-01.md v2 §4-4 · 서버 계약 docs/계약_장착후확인_서버_2026-10-02.md §A-1·§B-8
//  - 저장 = setClinicFeedbackPrefs(치과 **대표만**, 서버 requireOwner). 직원은 읽기 전용(같은 값 표시만)
//  - 읽기 = clinicPatientRecords/{clinicUid}.feedbackSettings 직접 get(rules: 오너·직원). 키 없음 = 계약 기본값(사용 꺼짐 · 3일·2주 켜짐 · 1개월·6개월 꺼짐)
//  - 켜기는 약관 v7.0(환자정보 처리위탁 사목) 재확인 전까지 서버가 거부(delegation-reconfirm-required) — 의도된 잠금, 안내 문구로 표시
//  - 자유 문구 입력 0 · 문항 수정 0(문항 세트는 서버 고정)
import React, { useEffect, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { getFeatures, isPatientFeedbackOn } from '../../utils/features';
import { POINTS, DEFAULT_FEEDBACK_SETTINGS, normalizeFeedbackSettings, readClinicFeedbackSettings, setClinicFeedbackPrefs, describeFeedbackError } from '../../services/clinicFeedbackApi';

const HELP = '장착 후 확인에 동의한 환자에게만 치과 이름으로 앱 알림이 갑니다(매일 오전 10시). 기공물이 도착 처리된 다음 날을 장착일로 보고 켠 시점에 질문합니다. 장착 건당 최대 3회, 환자가 답하면 그 시점은 끝납니다. 문항은 의뢰서 내용에 맞춰 자동으로 정해지며 수정할 수 없습니다.';
const POINT_TEXT = Object.freeze({ D3: '장착 3일 후', D14: '장착 2주 후', D30: '장착 1개월 후', D180: '장착 6개월 후' });

function ClinicFeedbackSettings({ user }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const isStaff = user?.userType === 'staff' || !!user?.companyId;
    const clinicUid = user?.companyId || user?.uid;
    const businessType = user?.businessType || user?.companyBusinessType;
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';

    const [flagOn, setFlagOn] = useState(null);       // null=로딩
    const [settings, setSettings] = useState(null);   // null=로딩
    const [loadErr, setLoadErr] = useState('');
    const [busyKey, setBusyKey] = useState('');
    const [notice, setNotice] = useState(null);       // { type:'ok'|'err', text }

    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFlagOn(isPatientFeedbackOn(f, user, user?.email)); })
            .catch((e) => { console.error('[장착 후 확인 설정] 플래그 조회 실패:', e?.message || e); if (alive) setFlagOn(false); });
        return () => { alive = false; };
    }, [user]);

    useEffect(() => {
        if (!flagOn || !clinicUid || !isClinic) return;
        let alive = true;
        readClinicFeedbackSettings(clinicUid)
            .then(s => { if (alive) setSettings(s); })
            .catch((e) => {
                console.error('[장착 후 확인 설정] 설정 조회 실패:', e?.code || e?.message || e);
                if (alive) { setSettings(normalizeFeedbackSettings(null)); setLoadErr('저장된 설정을 불러오지 못해 기본값을 표시합니다. 새로고침해 주세요.'); }
            });
        return () => { alive = false; };
    }, [flagOn, clinicUid, isClinic]);

    if (!isClinic) {
        return (
            <div style={S.container}>
                <PageHeader icon={ClipboardCheck} title="장착 후 확인" />
                <div style={S.emptyBox}>치과 계정만 사용할 수 있는 화면입니다.</div>
            </div>
        );
    }
    if (flagOn === false) {
        return (
            <div style={S.container}>
                <PageHeader icon={ClipboardCheck} title="장착 후 확인" />
                <div style={S.emptyBox}>장착 후 확인 기능이 아직 열리지 않았습니다.</div>
            </div>
        );
    }

    const cur = settings || normalizeFeedbackSettings(null);

    // key = 'enabled' | 'D3'… | 'other' — 바뀐 값만 반영해 전체(enabled+points 4키+units.other)를 보낸다
    const save = async (key) => {
        if (isStaff || busyKey || !settings) return;
        const base = { enabled: cur.enabled, points: cur.points, units: { other: cur.units.other } };
        const next = key === 'enabled' ? { ...base, enabled: !cur.enabled }
            : key === 'other' ? { ...base, units: { other: !cur.units.other } }
            : { ...base, points: { ...cur.points, [key]: !cur.points[key] } };
        setBusyKey(key); setNotice(null);
        try {
            const r = await setClinicFeedbackPrefs(next);
            setSettings(normalizeFeedbackSettings(r?.settings || next));
            setNotice({ type: 'ok', text: '저장했습니다.' });
        } catch (e) {
            setNotice({ type: 'err', text: describeFeedbackError(e, '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.') });
        } finally { setBusyKey(''); }
    };

    const sw = (key, label, on, sub) => {
        const disabled = isStaff || !!busyKey || !settings;
        return (
            <div key={key} style={S.item}>
                <span style={S.itemLabel}>{label}</span>
                <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => save(key)}
                    style={{ ...S.switch, background: on ? '#2563eb' : '#cbd5e1', cursor: disabled ? 'default' : 'pointer', opacity: busyKey === key ? 0.6 : 1 }}>
                    <span style={{ ...S.knob, transform: on ? 'translateX(20px)' : 'translateX(0)' }} />
                </button>
                <span style={{ ...S.state, color: on ? '#1d4ed8' : '#94a3b8' }}>{on ? '켜짐' : '꺼짐'}</span>
                {sub && <span style={S.sub}>{sub}</span>}
            </div>
        );
    };

    return (
        <div style={S.container}>
            <PageHeader icon={ClipboardCheck} title="장착 후 확인" />

            <div style={S.box}>
                <div style={S.sectionTitle}>장착 후 확인 사용</div>
                {isStaff && <div style={S.readonly}>직원 계정은 확인만 할 수 있습니다. 변경은 치과 대표 계정에서 합니다.</div>}
                {flagOn === null || settings === null ? (
                    <div style={S.help}>불러오는 중…</div>
                ) : (
                    <>
                        <div style={S.list}>{sw('enabled', '사용', cur.enabled, cur.enabled ? '' : '꺼져 있으면 새 질문이 만들어지지 않습니다')}</div>
                        <div style={{ ...S.sectionTitle, marginTop: 16 }}>질문할 시점</div>
                        <div style={S.list}>
                            {POINTS.map(p => sw(p, POINT_TEXT[p], cur.points[p], DEFAULT_FEEDBACK_SETTINGS.points[p] ? '기본 켜짐' : '기본 꺼짐'))}
                        </div>
                        <div style={{ ...S.sectionTitle, marginTop: 16 }}>질문할 항목</div>
                        <div style={S.list}>
                            {sw('other', '기타 항목도 묻기', cur.units.other, '기본 꺼짐 · 종류를 알 수 없는 기공물(기타)도 질문에 포함합니다')}
                        </div>
                    </>
                )}
                <div style={S.help}>{HELP}</div>
                {loadErr && <div style={S.err}>{loadErr}</div>}
                {notice && <div style={notice.type === 'ok' ? S.ok : S.err}>{notice.text}</div>}
            </div>
        </div>
    );
}

const S = {
    container: { padding: 20, maxWidth: 720, margin: '0 auto' },
    emptyBox: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 26, textAlign: 'center', fontSize: 14, color: '#9ca3af', background: '#fff' },
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    sectionTitle: { fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 10 },
    help: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginTop: 10 },
    readonly: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px', marginBottom: 10 },
    list: { display: 'flex', flexDirection: 'column', gap: 4 },
    item: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 4px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' },
    itemLabel: { fontSize: 13.5, fontWeight: 600, color: '#0f172a', minWidth: 110 },
    switch: { position: 'relative', width: 44, height: 24, borderRadius: 999, border: 'none', padding: 0, flexShrink: 0, transition: 'background .15s' },
    knob: { position: 'absolute', top: 2, left: 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.25)', transition: 'transform .15s' },
    state: { fontSize: 12, fontWeight: 600 },
    sub: { fontSize: 12, color: '#94a3b8' },
    ok: { marginTop: 10, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    err: { marginTop: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
};

export default ClinicFeedbackSettings;
